import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import { features, comparisonData, testimonials } from '@/mocks/categories';
import { Link } from 'react-router-dom';
import { useState } from 'react';

export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <main>
      {/* Schema.org JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Kakobuy Spreadsheet - The Ultimate Product Finds Database & Shopping Guide",
            "description": "Discover the latest Kakobuy Spreadsheet with organized product finds, shopping categories, comparison guides, and updated resources. Explore Kakobuy, CSSBuy, Superbuy and Oopbuy spreadsheets in one place.",
            "url": `${import.meta.env.VITE_SITE_URL}/`,
            "isPartOf": {
              "@type": "WebSite",
              "name": "KakobuyFinds",
              "url": import.meta.env.VITE_SITE_URL,
            },
            "about": {
              "@type": "Thing",
              "name": "Kakobuy Spreadsheet",
              "description": "Organized digital spreadsheet compiling thousands of product finds across multiple shopping categories including Shoes, Hoodies, T-Shirts, Jackets, Pants, Headwear, Sets, Underwear, Jersey, Accessories, Bags, Belts, Perfumes, and Other Stuff.",
            },
            "publisher": {
              "@type": "Organization",
              "name": "KakobuyFinds",
              "url": import.meta.env.VITE_SITE_URL,
              "logo": {
                "@type": "ImageObject",
                "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png",
              },
            },
          }),
        }}
      />
      <HeroSection />
      <CategoryGrid />

      {/* ==================== SEO Content Section ==================== */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center">
              What is <span className="text-primary-500">Kakobuy Spreadsheet</span>?
            </h2>
            <div className="mt-8 space-y-5 text-foreground-600 leading-relaxed">
              <p className="text-base">
                A <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> is an organized digital document that compiles thousands of product finds across multiple categories, making it easier for shoppers to discover, compare, and access quality items from global marketplaces. Instead of spending hours manually searching through countless listings, users can leverage these carefully curated spreadsheets to find what they need in seconds.
              </p>
              <p className="text-base">
                The <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> approach has become increasingly popular among online shoppers who value efficiency and organization. Each spreadsheet typically contains product names, direct links, category tags, pricing references, and quality notes — all structured in an easy-to-navigate format. Whether you are looking for sneakers, clothing, accessories, or electronics, these <strong className="text-foreground-900">spreadsheet finds</strong> cover virtually every shopping category imaginable.
              </p>
              <p className="text-base">
                What sets the <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> apart is its commitment to regular updates and comprehensive category coverage. With over 14 distinct product categories and hundreds of new additions weekly, users always have access to fresh <strong className="text-foreground-900">shopping spreadsheet</strong> data. The organized structure allows both beginners and experienced shoppers to navigate through product listings intuitively, saving valuable time while discovering trending items they might have otherwise missed.
              </p>
              <p className="text-base">
                Beyond just product listings, the <strong className="text-foreground-900">Kakobuy Spreadsheet</strong> ecosystem includes comparison resources that help users evaluate alternatives like CSSBuy Spreadsheet, Superbuy Spreadsheet, and Oopbuy Spreadsheet. This comprehensive approach to <strong className="text-foreground-900">product spreadsheet</strong> research empowers shoppers to make informed decisions based on their specific needs and preferences.
              </p>
            </div>
            <div className="mt-8 text-center">
              <Link
                to="/article/kakobuy-spreadsheet-complete-guide"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                Read the Complete Guide
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Feature Cards ==================== */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              Explore <span className="text-primary-500">Spreadsheet</span> Resources
            </h2>
            <p className="mt-2 text-sm text-foreground-500">Compare the best shopping spreadsheet platforms</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => (
              <div
                key={idx}
                className="bg-background-50 rounded-2xl p-6 border border-background-200/50 hover:border-primary-200 transition-all duration-300 group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500 mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-foreground-900 mb-2 font-heading">
                  {feature.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== Comparison Table ==================== */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              <span className="text-primary-500">Spreadsheet</span> Comparison
            </h2>
            <p className="mt-2 text-sm text-foreground-500">Find the right spreadsheet platform for your needs</p>
          </div>
          <div className="max-w-3xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground-800">Spreadsheet</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground-800">Best For</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground-800">Main Feature</th>
                  <th className="text-left py-4 px-4 text-sm font-semibold text-foreground-800">User Type</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr key={idx} className="border-b border-background-200/50 hover:bg-background-50 transition-colors">
                    <td className="py-4 px-4 text-sm font-semibold text-foreground-900 whitespace-nowrap">{row.name}</td>
                    <td className="py-4 px-4 text-sm text-foreground-600">{row.bestFor}</td>
                    <td className="py-4 px-4 text-sm text-foreground-600">{row.mainFeature}</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                        {row.userType}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ==================== About / Authority Section ==================== */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16 items-center">
            <div className="lg:w-[42%]">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-tight">
                Why Thousands Trust <span className="text-primary-500">Our Resources</span>
              </h2>
              <p className="mt-6 text-base text-foreground-600 leading-relaxed">
                We built KakobuyFinds to solve a real problem: finding quality products online takes too much time. Our spreadsheet resources eliminate the guesswork by organizing thousands of verified product finds into intuitive categories. Every listing is regularly checked and updated, ensuring you always have access to the latest and most reliable shopping information available.
              </p>
              <p className="mt-4 text-base text-foreground-600 leading-relaxed">
                Whether you are a beginner just getting started with spreadsheet-based product research or an experienced shopper looking to optimize your workflow, our resources are designed to adapt to your needs. The combination of organized categories, direct product links, and comparison tools creates a seamless discovery experience.
              </p>
              <div className="mt-8 flex items-center gap-12">
                <div>
                  <div className="text-3xl font-bold text-foreground-950 font-heading">14</div>
                  <div className="text-xs text-foreground-500 mt-1">Categories</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-foreground-950 font-heading">3,000+</div>
                  <div className="text-xs text-foreground-500 mt-1">Product Finds</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-foreground-950 font-heading">Weekly</div>
                  <div className="text-xs text-foreground-500 mt-1">Updates</div>
                </div>
              </div>
              <div className="mt-8">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                >
                  Explore Kakobuy Sheet
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="lg:w-[58%]">
              <div className="relative rounded-3xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Bright%20modern%20co%20working%20space%20with%20iMac%20displaying%20colorful%20product%20catalog%20grid%20on%20screen%2C%20small%20succulents%20and%20design%20books%20arranged%20on%20clean%20desk%2C%20natural%20light%20flooding%20from%20large%20windows%2C%20warm%20wood%20tones%20and%20white%20surfaces%2C%20creative%20professional%20atmosphere%2C%20editorial%20interior%20photography%2C%20clean%20inspiring%20workspace%2C%20plants%20and%20minimal%20decor%2C%20contemporary%20productive%20environment&width=900&height=700&seq=about-v2-kakobuy&orientation=landscape"
                  alt="Kakobuy Spreadsheet workspace and catalog"
                  className="w-full h-[420px] md:h-[520px] object-cover"
                  loading="lazy"
                />
                <div className="absolute top-6 left-6">
                  <span className="inline-flex px-4 py-1.5 rounded-full bg-background-50/95 text-foreground-800 text-xs font-semibold backdrop-blur-sm">
                    Updated Daily
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== Testimonials ==================== */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="max-w-[760px] mx-auto">
            <div className="text-center mb-4">
              <span className="font-heading text-sm text-accent-600 italic tracking-wide">( What Users Say )</span>
            </div>

            <div className="relative bg-background-50 rounded-[28px] p-8 md:p-12 border border-background-200/50">
              <div className="absolute -top-5 left-8 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-foreground-900 text-background-50 text-sm font-semibold">
                <i className="ri-star-fill text-accent-500 text-sm"></i>
                <span>{testimonials[activeTestimonial].rating}</span>
              </div>

              <div className="mt-4">
                <i className="ri-double-quotes-l text-4xl text-primary-200 leading-none"></i>
                <p className="mt-2 text-base md:text-lg text-foreground-700 leading-relaxed font-heading italic">
                  {testimonials[activeTestimonial].content}
                </p>
              </div>

              <div className="mt-8 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center">
                  <span className="text-accent-600 font-bold text-lg">
                    {testimonials[activeTestimonial].name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="text-sm font-semibold text-foreground-900">
                    {testimonials[activeTestimonial].name}
                  </div>
                  <div className="text-xs text-foreground-500">
                    {testimonials[activeTestimonial].role}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={prevTestimonial}
                className="w-10 h-10 flex items-center justify-center rounded-lg border border-background-300 text-foreground-600 hover:text-foreground-950 hover:border-foreground-300 transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
              </button>
              <button
                onClick={nextTestimonial}
                className="w-10 h-10 flex items-center justify-center rounded-lg bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-all cursor-pointer"
              >
                <i className="ri-arrow-right-line"></i>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ==================== CTA Section ==================== */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight">
            Ready to Discover Your <br className="hidden sm:block" />
            <span className="text-primary-500">Next Find</span>?
          </h2>
          <p className="mt-4 text-base text-foreground-500 max-w-xl mx-auto">
            Access thousands of organized product finds with the Kakobuy Spreadsheet. Updated weekly with fresh discoveries across all categories.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-full transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
            >
              Kakobuy Sheet
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-8 py-3.5 text-base font-semibold text-primary-600 border-2 border-primary-400 hover:bg-primary-50 rounded-full transition-all hover:scale-[1.02] whitespace-nowrap cursor-pointer"
            >
              Buy It Now
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
          </div>

          <div className="mt-14 flex items-center justify-center gap-6 flex-wrap opacity-40">
            {['Shoes', 'Hoodies', 'T-Shirts', 'Jackets', 'Bags', 'Watches', 'Accessories', 'Jerseys'].map((item) => (
              <span key={item} className="text-sm font-medium text-foreground-400 whitespace-nowrap">
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== Article CTA ==================== */}
      <section className="py-12 bg-background-100">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="bg-background-50 rounded-3xl p-8 md:p-12 border border-background-200/50">
            <div className="text-center mb-10">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                Explore Our <span className="text-primary-500">Kakobuy Spreadsheet</span> Guides
              </h3>
              <p className="mt-3 text-sm text-foreground-500 max-w-lg mx-auto">
                In-depth articles covering everything from getting started to advanced product research techniques.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { slug: 'kakobuy-spreadsheet-complete-guide', label: 'Complete Guide 2026', desc: 'Everything from basics to advanced tips', icon: 'ri-book-open-line' },
                { slug: 'kakobuy-spreadsheet-vs-cssbuy-superbuy', label: 'Platform Comparison', desc: 'Kakobuy vs CSSBuy vs Superbuy', icon: 'ri-scales-3-line' },
                { slug: 'kakobuy-spreadsheet-for-beginners', label: 'Beginner\'s Guide', desc: 'Your first 30 days step by step', icon: 'ri-seedling-line' },
                { slug: 'best-kakobuy-spreadsheet-finds-2026', label: 'Best Finds 2026', desc: 'Top products across all categories', icon: 'ri-star-line' },
              ].map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="flex flex-col gap-3 p-5 rounded-2xl bg-background-100 hover:bg-primary-50 border border-background-200/50 hover:border-primary-200 transition-all duration-300 group"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 group-hover:bg-accent-100 transition-colors">
                    <i className={`${article.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors">
                      {article.label}
                    </h4>
                    <p className="text-xs text-foreground-400 mt-1">{article.desc}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-primary-500 mt-auto">
                    Read Article
                    <i className="ri-arrow-right-line text-xs"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}